import React, { useState } from 'react';
import './Contact.css';

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface ApiResponse {
  success: boolean;
  message?: string;
  error?: string;
  details?: string[];
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing again
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Get API URL from environment or use default for local development
      const apiUrl = process.env.REACT_APP_API_URL || '/api/contact';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result: ApiResponse = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', company: '', message: '' });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        // Handle error response
        const errorMsg = result.error || 'Failed to send message. Please try again.';
        const details = result.details ? `\n${result.details.join('\n')}` : '';
        setErrorMessage(errorMsg + details);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(
        'Unable to send message. Please email us directly at yang.chen@moosera.llc'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="contact-container">
        <div className="contact-content">
          <div className="contact-info">
            <h2 className="section-title">Let's Build Something Amazing</h2>
            <p className="contact-description">
              Ready to transform your business with AI? Whether you're just starting your 
              AI journey or looking to scale existing initiatives, we're here to help.
            </p>
            <div className="contact-details">
              <div className="contact-detail">
                <span className="detail-icon">📧</span>
                <div>
                  <h4>Email</h4>
                  <p>
                    <a href="mailto:yang.chen@moosera.llc" style={{ color: 'inherit', textDecoration: 'none' }}>
                      yang.chen@moosera.llc
                    </a>
                  </p>
                </div>
              </div>
              <div className="contact-detail">
                <span className="detail-icon">💼</span>
                <div>
                  <h4>Business Hours</h4>
                  <p>Monday - Friday, 9:00 AM - 6:00 PM EST</p>
                </div>
              </div>
              <div className="contact-detail">
                <span className="detail-icon">🌐</span>
                <div>
                  <h4>Location</h4>
                  <p>Serving clients globally</p>
                </div>
              </div>
            </div>
            <div className="contact-benefits">
              <h3>What to Expect</h3>
              <ul>
                <li>✓ Free initial consultation (30 minutes)</li>
                <li>✓ Custom AI roadmap proposal</li>
                <li>✓ Technical feasibility assessment</li>
                <li>✓ Transparent pricing and timelines</li>
              </ul>
            </div>
          </div>
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <h3>Get in Touch</h3>
              
              {isSubmitted && (
                <div className="form-message form-success">
                  ✓ Message sent successfully! We'll get back to you soon.
                </div>
              )}
              
              {errorMessage && (
                <div className="form-message form-error">
                  ⚠ {errorMessage}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="John Doe"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  placeholder="john@company.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  placeholder="Your Company Name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Tell us about your project *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  rows={5}
                  placeholder="Describe your AI project goals and requirements..."
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={isSubmitting || isSubmitted}
              >
                {isSubmitting ? 'Sending...' : isSubmitted ? '✓ Message Sent!' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
